import React, { memo, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Lock } from 'lucide-react';
import { TextNodeData } from '../../types';

// Color mapping for light/dark mode background colors
const NODE_COLOR_MAP: Record<string, { light: string; dark: string }> = {
  '': { light: '', dark: '' }, // Default - use CSS classes
  'f1f5f9': { light: '#f1f5f9', dark: '#334155' },
  'dbeafe': { light: '#dbeafe', dark: '#1e3a5f' },
  'dcfce7': { light: '#dcfce7', dark: '#14532d' },
  'f3e8ff': { light: '#f3e8ff', dark: '#3b0764' },
  'fef3c7': { light: '#fef3c7', dark: '#78350f' },
  'ffe4e6': { light: '#ffe4e6', dark: '#4c0519' },
  'cffafe': { light: '#cffafe', dark: '#164e63' },
};

const TextNode = ({
  id,
  data,
  selected,
}: NodeProps<TextNodeData>) => {
  const { setNodes } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(data.label);
  const [editDescription, setEditDescription] = useState(data.description || '');
  const labelInputRef = useRef<HTMLInputElement>(null);

  // Sync local state when data changes externally
  useEffect(() => {
    setEditLabel(data.label);
    setEditDescription(data.description || '');
  }, [data.label, data.description]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && labelInputRef.current) {
      labelInputRef.current.focus();
      labelInputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    setIsEditing(true);
  };

  const handleSave = useCallback(() => {
    if (data.onSnapshot) {
      data.onSnapshot();
    }
    setNodes((nodes) =>
      nodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              label: editLabel.trim() || 'Text',
              description: editDescription.trim() || undefined,
            },
          };
        }
        return node;
      })
    );
    setIsEditing(false);
  }, [id, setNodes, editLabel, editDescription, data]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setEditLabel(data.label);
      setEditDescription(data.description || '');
      setIsEditing(false);
    }
    e.stopPropagation();
  };

  // Normalize hex: Ensure it starts with # for CSS
  let displayColor = data.hex;

  // If no color set, use defaults (Selected: Blue, Idle: Slate)
  if (!displayColor) {
    displayColor = selected ? '#3b82f6' : '#94a3b8';
  } else if (!displayColor.startsWith('#')) {
    displayColor = '#' + displayColor;
  }

  // Node customization
  const showBorder = data.showBorder !== false;
  const nodeColor = data.nodeColor || '';
  const borderColor = data.borderColor;
  const sizeMod = data.sizeMod || 'default';

  // Size classes based on sizeMod
  const sizeClasses = {
    small: 'w-[160px] px-3 py-3',
    default: 'w-[196px] px-4 py-4',
    large: 'w-[240px] px-5 py-5',
  };

  // Border style class
  const getBorderClass = useCallback(() => {
    if (!showBorder) {
      return 'border-transparent';
    }
    if (data.locked) {
      return 'border-amber-400/50 dark:border-amber-500/50';
    }
    if (borderColor) {
      return ''; // Will use inline style
    }
    return 'border-border hover:border-primary/60';
  }, [showBorder, data.locked, borderColor]);

  // Get background color config
  const colorConfig = NODE_COLOR_MAP[nodeColor];
  const hasCustomBg = nodeColor && colorConfig && colorConfig.light;

  // Combined inline styles for border
  const nodeStyles = useMemo(() => {
    const styles: React.CSSProperties = {};
    
    // Border color - use custom border color or display color
    if (showBorder && !data.locked) {
      if (borderColor) {
        styles.borderColor = `#${borderColor}`;
      } else if (!getBorderClass()) {
        styles.borderColor = displayColor;
      }
    }
    
    return styles;
  }, [showBorder, borderColor, data.locked, displayColor, getBorderClass]);

  return (
    <div
      className={`group relative shadow-sm !rounded-none border-2 transition-all duration-200 ${sizeClasses[sizeMod]} ${getBorderClass()} ${hasCustomBg ? 'node-themed-bg' : 'bg-card text-card-foreground'}`}
      style={{
        ...nodeStyles,
        ...(hasCustomBg ? { '--bg-light': colorConfig.light, '--bg-dark': colorConfig.dark } as React.CSSProperties : {}),
      }}
      onDoubleClick={handleDoubleClick}
    >
      {data.locked && (
        <div className="absolute -top-2 -right-2 w-5 h-5 bg-amber-400 dark:bg-amber-500 rounded-full flex items-center justify-center shadow-sm">
          <Lock className="w-3 h-3 text-white" />
        </div>
      )}

      {isEditing ? (
        <div className="flex flex-col">
          <input
            ref={labelInputRef}
            type="text"
            value={editLabel}
            onChange={(e) => setEditLabel(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="font-bold text-center text-sm text-wrap leadning-none bg-transparent border-none focus:outline-none focus:border-none text-card-foreground w-full"
            placeholder="Label"
          />
        </div>
      ) : (
        <div className="flex flex-col overflow-visible items-center cursor-text min-w-0">
          <span className="font-bold text-sm text-wrap leading-[1.4] text-card-foreground">
            {data.label}
          </span>
        </div>
      )}

      {/* Single handle per side - connectionMode="loose" allows bidirectional connections */}
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: displayColor,
          top: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: displayColor,
          left: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: displayColor,
          bottom: '-6px',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className="!w-3 !h-3 transition-colors border bg-card border-border dark:bg-card dark:border-border"
        style={{
          backgroundColor: displayColor,
          right: '-6px',
        }}
      />
    </div>
  );
};

export default memo(TextNode);
